import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { of, Subject } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import type { OrderItemPreviewRequest } from "../../card-orders/card-orders.interface";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { PrintCardService } from "../../print-cards/print-card.service";
import type { PrintCardOrderItemPreviewResult } from "../../print-cards/print-cards.interface";
import { PrintCardOrdersService } from "../print-card-orders.service";

@Component({
  selector: "app-print-card-order-item-preview-dialog",
  templateUrl: "./print-card-order-item-preview-dialog.component.html",
  styleUrls: ["./print-card-order-item-preview-dialog.component.scss"],
  standalone: false,
})
export class PrintCardOrderItemPreviewDialogComponent
  implements OnInit, OnDestroy
{
  frontDocumentHtml: string | null = null;
  backDocumentHtml: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;
  loading = true;
  pageWidthCm: number | null = null;
  pageHeightCm: number | null = null;
  showingPdf = false;

  private readonly destroy$ = new Subject<void>();
  private objectUrl: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: OrderItemPreviewRequest,
    private readonly dialogRef: MatDialogRef<PrintCardOrderItemPreviewDialogComponent>,
    private readonly printCardService: PrintCardService,
    private readonly printCardOrdersService: PrintCardOrdersService,
    private readonly toastr: ToastrTranslatedService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    if (this.data.mode === "order") {
      this.loadOrderPreview(this.data);
      return;
    }
    this.loadDraftPreview(this.data);
  }

  ngOnDestroy(): void {
    this.clearObjectUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  get isPdfPreview(): boolean {
    return this.showingPdf;
  }

  private loadOrderPreview(
    data: Extract<OrderItemPreviewRequest, { mode: "order" }>,
  ): void {
    const pdfRequest$ =
      data.previewApi === "admin"
        ? this.printCardOrdersService.getItemPdf(data.orderId, data.itemId)
        : this.printCardService.getOrderItemPdf(data.orderId, data.itemId);

    pdfRequest$
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((blob) => {
        if (
          blob &&
          blob.size > 0 &&
          (!blob.type ||
            blob.type === "application/pdf" ||
            blob.type === "application/octet-stream")
        ) {
          this.clearObjectUrl();
          this.objectUrl = URL.createObjectURL(
            new Blob([blob], { type: "application/pdf" }),
          );
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            this.objectUrl,
          );
          this.frontDocumentHtml = null;
          this.backDocumentHtml = null;
          this.showingPdf = true;
          this.loading = false;
          return;
        }
        this.loadOrderHtmlPreview(data);
      });
  }

  private loadOrderHtmlPreview(
    data: Extract<OrderItemPreviewRequest, { mode: "order" }>,
  ): void {
    const snapshotRequest$ =
      data.previewApi === "admin"
        ? this.printCardOrdersService.getItemPreview(data.orderId, data.itemId)
        : this.printCardService.getOrderItemPreview(data.orderId, data.itemId);

    snapshotRequest$
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((preview) => {
        if (!preview) {
          this.clearObjectUrl();
          this.frontDocumentHtml = null;
          this.backDocumentHtml = null;
          this.pageWidthCm = null;
          this.pageHeightCm = null;
          this.pdfUrl = null;
          this.showingPdf = false;
          this.loading = false;
          this.toastr.error("printCardOrders.previewFailed");
          return;
        }
        this.showHtmlPreview(preview);
      });
  }

  private loadDraftPreview(
    data: Extract<OrderItemPreviewRequest, { mode: "draft" }>,
  ): void {
    const templateId = data.templateId.trim();
    const employeeEmail = data.employeeEmail.trim();
    if (!templateId || !employeeEmail) {
      this.clearObjectUrl();
      this.frontDocumentHtml = null;
      this.backDocumentHtml = null;
      this.pageWidthCm = null;
      this.pageHeightCm = null;
      this.pdfUrl = null;
      this.showingPdf = false;
      this.loading = false;
      this.toastr.error("printCardOrders.previewFailed");
      return;
    }

    this.printCardService
      .preview(templateId, employeeEmail, data.pageSizePresetId)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((preview) => {
        if (!preview) {
          this.clearObjectUrl();
          this.frontDocumentHtml = null;
          this.backDocumentHtml = null;
          this.pageWidthCm = null;
          this.pageHeightCm = null;
          this.pdfUrl = null;
          this.showingPdf = false;
          this.loading = false;
          this.toastr.error("printCardOrders.previewFailed");
          return;
        }
        this.showHtmlPreview(preview);
      });
  }

  private showHtmlPreview(preview: PrintCardOrderItemPreviewResult): void {
    this.clearObjectUrl();
    this.pdfUrl = null;
    this.showingPdf = false;
    const hasDims =
      Number.isFinite(preview.pageWidthCm) &&
      Number.isFinite(preview.pageHeightCm) &&
      preview.pageWidthCm > 0 &&
      preview.pageHeightCm > 0;
    this.pageWidthCm = hasDims ? preview.pageWidthCm : null;
    this.pageHeightCm = hasDims ? preview.pageHeightCm : null;
    const front = preview.frontDocumentHtml.trim();
    this.frontDocumentHtml = front.length ? front : null;
    this.backDocumentHtml = preview.backDocumentHtml.trim().length
      ? preview.backDocumentHtml
      : null;
    this.loading = false;
  }

  private clearObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
